import { useState, useCallback } from "react";
import { useSnackbar } from "notistack";

import CommonConstants from "../../../constants";

/**
 * Notification
 */

export type Notification = {
  id?: number;
  type: 'info' | 'warn' | 'error';
  title: string;
  message: string;
  autoFinish?: boolean;
  link?: string;
};

/**
 * usePageContentContextViewModel
 */

export const usePageContentContextViewModel = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [pageLoading, setPageLoading] = useState(false);
  const [pageSaving, setPageSaving] = useState(false);

  const [actionBarVisibility, setActionBarVisibility] = useState<boolean>(false);

  const [customLoading, setCustomLoading] = useState(false);
  const [customLoadingMessage, setCustomLoadingMessage] = useState('Carregando...');

  const setPageActionBarVisibility = useCallback((visibility: boolean) => {
    setActionBarVisibility(visibility);
  }, [
  ]);

  const pushNotification = useCallback((notification: Notification) => {
    let variant = 'default';

    switch (notification.type) {
      case 'info':
        variant = 'success';
        break;

      case 'warn':
        variant = 'warning';
        break;

      case 'error':
        variant = 'error';
        break;

      default:
        break;
    }

    enqueueSnackbar({
      variant: variant as any,
      message: notification.message,
      autoHideDuration: notification.autoFinish ? CommonConstants.notificationTime : null,
    });
  }, [
    enqueueSnackbar,
  ]);

  const pushInfoNotification = useCallback((
    title: string,
    message: string,
    opts?: {
      link?: string,
      isAutoFinish?: boolean,
    }) => {
    const isAutoFinish = opts?.isAutoFinish ?? true;
    enqueueSnackbar({
      variant: "success",
      message: message,
      autoHideDuration: isAutoFinish ? CommonConstants.successNotificationTime : null,
    });
  }, [
    enqueueSnackbar,
  ]);

  const pushWarnNotification = useCallback((
    title: string,
    message: string,
    opts?: {
      link?: string,
      isAutoFinish?: boolean,
    }) => {
    const isAutoFinish = opts?.isAutoFinish ?? true;
    enqueueSnackbar({
      variant: "warning",
      message: message,
      autoHideDuration: isAutoFinish ? CommonConstants.warningNotificationTime : null,
      link: opts?.link ?? undefined,
    } as any);
  }, [
    enqueueSnackbar,
  ]);

  const pushErrorNotification = useCallback((
    title: string,
    message: string[] | string,
    opts?: {
      link?: string,
      isAutoFinish?: boolean,
    }) => {
    const isAutoFinish = opts?.isAutoFinish ?? true;
    if (typeof message === 'string') {
      enqueueSnackbar({
        variant: "error",
        message: message,
        autoHideDuration: isAutoFinish === true ? CommonConstants.notificationTime : null,
      });
    } else if (typeof message === 'object' && message instanceof Array) {
      for (let i = 0; i < message.length; i++) {
        enqueueSnackbar({
          variant: "error",
          message: message[i],
          autoHideDuration: isAutoFinish === true ? CommonConstants.notificationTime : null,
        });
      }
    }
  }, [
    enqueueSnackbar,
  ]);

  const handleCustomLoading = useCallback((loadingState: boolean, message?: string) => {
    if (message) {
      setCustomLoadingMessage(message);
    } else {
      setCustomLoadingMessage("Carregando...");
    }
    setCustomLoading(loadingState);
  }, [
  ]);

  return {
    pageLoading,
    pageSaving,
    customLoading,
    customLoadingMessage,
    actionBarVisibility,
    setPageActionBarVisibility,
    setPageLoading,
    setPageSaving,
    pushNotification,
    pushInfoNotification,
    pushWarnNotification,
    pushErrorNotification,
    handleCustomLoading,
  };
}